import React, { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableRow, TableHead, TableContainer,
  Paper, Box, Pagination, Chip, Typography, IconButton, Tooltip, Button,
  Dialog, DialogContent, DialogActions, TextField, MenuItem, Select,
  FormControl, InputLabel, Grid, Switch, FormControlLabel, Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LockIcon from "@mui/icons-material/Lock";
import VerifiedIcon from "@mui/icons-material/Verified";
import Swal from "sweetalert2";
import jwt_decode from "jwt-decode";
import { useSelector } from "react-redux";

import CustomizedButtons from "../../components/button/button";
import { formatISTDateTime, getFormattedValue, getTrancationType, maskPhone } from "../../helper";
import ViewTranscation from "./ViewTransaction";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";

const TXN_TYPES = ["DEPOSIT","WITHDRAW","CHIP_ISSUE","LOAN_DISBURSE","CHIP_CASHOUT","LOAN_REPAY","CHIP_EXCHANGE","REFERRAL_CASHBACK","RAKE","EXPENSE","BONUS_HOLDBACK"];
const PAYMENT_MODES = ["CASH","BANK","WALLET","OTHER"];
const OFFER_STATUSES = ["NA","PENDING","APPROVED","REJECTED","AUTO"];

export const GameTable = ({ gameTranscation, pagination, getGameTranscation }) => {

  const [viewDialog, setViewDialog] = useState(false);
  const [selectTranscation, setSelectTranscation] = useState({});

  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  const [payStatusLoading, setPayStatusLoading] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(null); // txn _id being verified

  const [markPaidDialog, setMarkPaidDialog] = useState(null); // txn object
  const [markPaidRemark, setMarkPaidRemark] = useState("");
  const [markPaidAccountId, setMarkPaidAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);

  const token = useSelector((state) => state.loginSlice.token);

  useEffect(() => {
    axiosRequest("GET", `${apiBaseUrl}/accounts`, token).then((r) => setAccounts(r?.data?.data || [])).catch(() => {});
  }, [token]);

  const isMainAdmin = (() => {
    try { return jwt_decode(token)?.accessLevel >= 100; }
    catch { return false; }
  })();

  const handleChange = (event, value) => { getGameTranscation(value); };

  const handleView = (data) => {
    setSelectTranscation(data);
    setViewDialog(true);
  };

  const handleEditOpen = (txn) => {
    setEditData({
      _id:                    txn._id,
      user:                   txn.user_id || {},
      txn_amount:             txn.txn_amount || 0,
      payment_mode:           txn.payment_mode || "CASH",
      txn_type:               txn.txn_type || "",
      type:                   txn.type || "CR",
      cashback_amount:        txn.cashback_amount || 0,
      offer_approval_status:  txn.offer_approval_status || "NA",
      offer_approval_required: txn.offer_approval_required || false,
      txn_date:               txn.txn_date ? new Date(txn.txn_date).toISOString().slice(0, 16) : "",
      depositCategory:        txn.depositCategory || "",
    });
    setEditDialog(true);
  };

  const handleEditSave = async () => {
    try {
      setEditLoading(true);
      const payload = {
        txn_amount:             Number(editData.txn_amount),
        payment_mode:           editData.payment_mode,
        txn_type:               editData.txn_type,
        type:                   editData.type,
        cashback_amount:        Number(editData.cashback_amount),
        offer_approval_status:  editData.offer_approval_status,
        offer_approval_required: editData.offer_approval_required,
        txn_date:               editData.txn_date,
        depositCategory:        editData.depositCategory,
      };
      const res = await axiosRequest("PATCH", `${apiBaseUrl}/transaction/${editData._id}`, token, payload);
      if (res?.status === "success") {
        Swal.fire({ icon: "success", title: "Transaction Updated", timer: 1500, showConfirmButton: false });
        setEditDialog(false);
        getGameTranscation(pagination.currentPage);
      } else {
        Swal.fire({ icon: "error", title: res?.message || "Update failed", timer: 2000, showConfirmButton: false });
      }
    } catch (e) {
      Swal.fire({ icon: "error", title: "Error", timer: 2000, showConfirmButton: false });
    } finally {
      setEditLoading(false);
    }
  };

  const handleMarkPaid = (txn) => {
    setMarkPaidRemark("");
    setMarkPaidAccountId("");
    setMarkPaidDialog(txn);
  };

  const handleMarkPaidConfirm = async () => {
    if (!markPaidRemark.trim()) return;
    const txn = markPaidDialog;
    setMarkPaidDialog(null);
    setPayStatusLoading(txn._id);
    try {
      const res = await axiosRequest("PATCH", `${apiBaseUrl}/transaction/${txn._id}`, token, {
        payment_status: "SUCCESS",
        paid_remark: markPaidRemark.trim(),
        account_id: markPaidAccountId || undefined,
      });
      if (res?.status === "success") {
        Swal.fire({ icon: "success", title: "Marked as Paid", timer: 1200, showConfirmButton: false });
        getGameTranscation(pagination.currentPage);
      } else {
        Swal.fire({ icon: "error", title: res?.message || "Failed", timer: 2000, showConfirmButton: false });
      }
    } catch {
      Swal.fire({ icon: "error", title: "Error", timer: 2000, showConfirmButton: false });
    } finally {
      setPayStatusLoading(null);
    }
  };

  const handleVerify = async (txn) => {
    const result = await Swal.fire({
      title: "Verify Transaction?",
      html: `Mark <b>${Number(txn.txn_amount || 0).toLocaleString("en-IN")}</b> ${txn.txn_type} for <b>${txn.user_id?.name || "Player"}</b> as verified?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2e7d32",
      confirmButtonText: "Verify",
    });
    if (!result.isConfirmed) return;
    setVerifyLoading(txn._id);
    try {
      const res = await axiosRequest("PATCH", `${apiBaseUrl}/transaction/${txn._id}/verify`, token);
      if (res?.status === "success") {
        Swal.fire({ icon: "success", title: "Verified", timer: 1200, showConfirmButton: false });
        getGameTranscation(pagination.currentPage);
      } else {
        Swal.fire({ icon: "error", title: res?.message || "Failed", timer: 2000, showConfirmButton: false });
      }
    } catch {
      Swal.fire({ icon: "error", title: "Error", timer: 2000, showConfirmButton: false });
    } finally {
      setVerifyLoading(null);
    }
  };

  const handleOfferApproval = async (txn, action) => {
    const label = action === "APPROVE" ? "Approve" : "Reject";
    const result = await Swal.fire({
      title: `${label} Cashback?`,
      html: `Cashback <b>${Number(txn.cashback_amount || 0).toLocaleString("en-IN")}</b> for <b>${txn.user_id?.name || "Player"}</b>`,
      icon: action === "APPROVE" ? "question" : "warning",
      showCancelButton: true,
      confirmButtonColor: action === "APPROVE" ? "#2e7d32" : "#d32f2f",
      confirmButtonText: label,
    });
    if (!result.isConfirmed) return;
    const res = await axiosRequest("PATCH", `${apiBaseUrl}/transaction/${txn._id}/offer-approval`, token, { action });
    if (res?.status === "success") {
      Swal.fire({ icon: "success", title: action === "APPROVE" ? "Cashback Approved" : "Offer Rejected", timer: 1500, showConfirmButton: false });
      getGameTranscation(pagination.currentPage);
    } else {
      Swal.fire({ icon: "error", title: res?.message || "Failed", timer: 2000, showConfirmButton: false });
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 2, px: 3, mb: 4 }}>

      <Paper elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f4f6f8" }}>
                <TableCell align="center" sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Txn Id</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Player</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Cr/Db</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Mode</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Cashback</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Offer Approval</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Payment</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Paid to Account</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Verified</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Action</TableCell>
                {isMainAdmin && <TableCell align="center" sx={{ fontWeight: 600 }}>Edit</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {gameTranscation.map((data, index) => {
                const crDb = data?.type || (data?.txn_type === "WITHDRAW" || data?.txn_type === "CHIP_ISSUE" || data?.txn_type === "LOAN_REPAY" ? "DB" : "CR");
                const txnType = { text: data?.txn_type || getTrancationType(data?.added_type).text, amountType: crDb };

                return (
                  <TableRow key={data._id || index} hover sx={{ transition: "0.2s", "&:hover": { backgroundColor: "#fafafa" } }}>

                    <TableCell align="center">
                      {pagination.size * (pagination.currentPage - 1) + index + 1}
                    </TableCell>

                    <TableCell align="center" sx={{ maxWidth: 140 }}>
                      <Typography fontWeight={500} fontSize={12} title={data.local_txn_id}
                        sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {data.local_txn_id}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Typography fontWeight={600} fontSize={13}>{data.user_id?.name || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.user_id?.playerId || ""}{data.user_id?.playerId && maskPhone(data.user_id?.phone) ? " · " : ""}{maskPhone(data.user_id?.phone) || ""}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Chip label={txnType.amountType} color={txnType.amountType === "CR" ? "success" : "error"} size="small" />
                    </TableCell>

                    <TableCell align="center">{txnType.text}</TableCell>

                    <TableCell align="center">{data.payment_mode || "—"}</TableCell>

                    <TableCell align="center">
                      <Typography fontWeight={600} color="primary">
                        {getFormattedValue(["DEPOSIT", "CHIP_ISSUE"].includes(data?.txn_type) ? (data?.txn_amount || 0) / 100 : data?.txn_amount)}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      {data.cashback_amount > 0
                        ? <Typography fontWeight={600} color="secondary.main">{data.cashback_amount.toLocaleString()}</Typography>
                        : <Typography color="text.disabled">—</Typography>}
                    </TableCell>

                    <TableCell align="center">
                      {data.offer_approval_status === "PENDING" ? (
                        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                          <Chip label="PENDING" size="small" color="warning" />
                          {isMainAdmin && (
                            <Box display="flex" gap={0.5} mt={0.5}>
                              <Tooltip title="Approve cashback">
                                <IconButton size="small" color="success" onClick={() => handleOfferApproval(data, "APPROVE")}>
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject cashback">
                                <IconButton size="small" color="error" onClick={() => handleOfferApproval(data, "REJECT")}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      ) : data.offer_approval_status === "APPROVED" ? (
                        <Chip label="APPROVED" size="small" color="success" />
                      ) : data.offer_approval_status === "REJECTED" ? (
                        <Chip label="REJECTED" size="small" color="error" />
                      ) : data.offer_approval_status === "AUTO" ? (
                        <Chip label="AUTO" size="small" color="info" variant="outlined" />
                      ) : (
                        <Typography color="text.disabled">—</Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">{formatISTDateTime(data?.txn_date)}</TableCell>

                    <TableCell align="center">
                      {(data.payment_mode === "BANK" || data.payment_mode === "OTHER") ? (
                        data.payment_status === "SUCCESS" ? (
                          <Chip
                            icon={<LockIcon sx={{ fontSize: 14 }} />}
                            label="PAID"
                            size="small"
                            color="success"
                          />
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={payStatusLoading === data._id}
                            onClick={() => handleMarkPaid(data)}
                            sx={{ fontSize: 11, px: 1, py: 0.3, minWidth: 80 }}
                          >
                            {payStatusLoading === data._id ? "..." : "Mark Paid"}
                          </Button>
                        )
                      ) : (
                        <Typography color="text.disabled" fontSize={12}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell align="center" sx={{ maxWidth: 140 }}>
                      {data.details?.paid_account_name ? (
                        <Tooltip title={data.details.paid_account_name + (data.details.paid_account_phone ? ` — ${data.details.paid_account_phone}` : "")}>
                          <Typography variant="caption" sx={{ color: "#1b5e20", fontWeight: 600, cursor: "default",
                            display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>
                            {data.details.paid_account_name}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography color="text.disabled" fontSize={12}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      {["DEPOSIT", "WITHDRAW"].includes(data.txn_type) ? (
                        data.is_verified ? (
                          <Tooltip title={`Verified by ${data.verified_by?.name || "Admin"}${data.verified_at ? " on " + formatISTDateTime(data.verified_at) : ""}`}>
                            <Box display="flex" flexDirection="column" alignItems="center" gap={0.3}>
                              <VerifiedIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
                              <Typography variant="caption" color="success.main" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                                {data.verified_by?.name || "Verified"}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            disabled={verifyLoading === data._id}
                            onClick={() => handleVerify(data)}
                            sx={{ fontSize: 11, px: 1, py: 0.3, minWidth: 70 }}
                          >
                            {verifyLoading === data._id ? "..." : "Verify"}
                          </Button>
                        )
                      ) : (
                        <Typography color="text.disabled" fontSize={12}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <CustomizedButtons label="Details" variant="outlined" size="small" onClick={() => handleView(data)} />
                    </TableCell>

                    {isMainAdmin && (
                      <TableCell align="center">
                        <Tooltip title="Edit Transaction">
                          <IconButton size="small" color="primary" onClick={() => handleEditOpen(data)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}

                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          count={pagination.totalPage}
          page={pagination.currentPage}
          onChange={handleChange}
          color="primary"
          shape="rounded"
        />
      </Box>

      {/* VIEW DIALOG */}
      <ViewTranscation
        open={viewDialog}
        handleClose={() => { setViewDialog(false); setSelectTranscation({}); }}
        data={selectTranscation}
        onUpdate={() => getGameTranscation(pagination.currentPage)}
      />

      {/* EDIT DIALOG */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>

        {/* Header */}
        <Box sx={{ background: "linear-gradient(135deg,#1e3c72,#2a5298)", px: 3, py: 2.5, color: "#fff" }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)" }}><PersonIcon /></Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>{editData.user?.name || "Player"}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                ID: {editData.user?.playerId || "—"} &nbsp;|&nbsp; {maskPhone(editData.user?.phone) || ""}
              </Typography>
            </Box>
            <Box ml="auto">
              <Chip
                label={editData.type === "CR" ? "CREDIT" : "DEBIT"}
                size="small"
                sx={{ background: editData.type === "CR" ? "#2e7d32" : "#c62828", color: "#fff", fontWeight: 700 }}
              />
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
            Changing the amount updates only the <b>difference</b> in the player's wallet.
          </Typography>
          <Grid container spacing={2} mt={0.5}>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Amount" type="number"
                value={editData.txn_amount}
                onChange={(e) => setEditData((p) => ({ ...p, txn_amount: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Mode</InputLabel>
                <Select value={editData.payment_mode || ""} label="Payment Mode"
                  onChange={(e) => setEditData((p) => ({ ...p, payment_mode: e.target.value }))}>
                  {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Txn Type</InputLabel>
                <Select value={editData.txn_type || ""} label="Txn Type"
                  onChange={(e) => setEditData((p) => ({ ...p, txn_type: e.target.value }))}>
                  {TXN_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>CR / DB</InputLabel>
                <Select value={editData.type || ""} label="CR / DB"
                  onChange={(e) => setEditData((p) => ({ ...p, type: e.target.value }))}>
                  <MenuItem value="CR">CR (Credit)</MenuItem>
                  <MenuItem value="DB">DB (Debit)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Cashback Amount" type="number"
                value={editData.cashback_amount}
                onChange={(e) => setEditData((p) => ({ ...p, cashback_amount: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Offer Approval Status</InputLabel>
                <Select value={editData.offer_approval_status || ""} label="Offer Approval Status"
                  onChange={(e) => setEditData((p) => ({ ...p, offer_approval_status: e.target.value }))}>
                  {OFFER_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editData.offer_approval_required}
                    onChange={(e) => setEditData((p) => ({ ...p, offer_approval_required: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Offer Approval Required"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Deposit Category"
                value={editData.depositCategory || ""}
                onChange={(e) => setEditData((p) => ({ ...p, depositCategory: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Txn Date & Time"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editData.txn_date || ""}
                onChange={(e) => setEditData((p) => ({ ...p, txn_date: e.target.value }))}
              />
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setEditDialog(false)} disabled={editLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editLoading}>
            {editLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MARK PAID REMARK DIALOG */}
      <Dialog open={!!markPaidDialog} onClose={() => setMarkPaidDialog(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ background: "linear-gradient(135deg,#1e3c72,#2a5298)", px: 3, py: 2, color: "#fff" }}>
          <Typography variant="h6" fontWeight={700}>Mark as Paid</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {Number(markPaidDialog?.txn_amount || 0).toLocaleString("en-IN")} — {markPaidDialog?.user_id?.name || "Player"}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            size="small"
            label="Remark"
            placeholder="e.g. Payment received via UPI"
            value={markPaidRemark}
            onChange={(e) => setMarkPaidRemark(e.target.value)}
            multiline
            rows={2}
            required
            error={!markPaidRemark.trim()}
            helperText={!markPaidRemark.trim() ? "Remark is required" : ""}
          />
          {["WITHDRAW", "EXPENSE"].includes(markPaidDialog?.txn_type) && markPaidDialog?.payment_mode === "OTHER" && accounts.length > 0 && (
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel>Link to Account (optional)</InputLabel>
              <Select
                value={markPaidAccountId}
                label="Link to Account (optional)"
                onChange={(e) => setMarkPaidAccountId(e.target.value)}
              >
                <MenuItem value="">— None —</MenuItem>
                {accounts.map((a) => (
                  <MenuItem key={a._id} value={a._id}>{a.name}{a.phone ? ` — ${a.phone}` : ""}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setMarkPaidDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleMarkPaidConfirm} disabled={!markPaidRemark.trim()}>
            Confirm Paid
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};
